import React from 'react';
import type { FatigueMetrics } from '../types';
import { fatigueColor } from '../utils/helpers';
import { AlertTriangle, Shield, Info, AlertOctagon } from 'lucide-react';

interface Props {
  fatigue: FatigueMetrics;
}

export default function FatigueMeter({ fatigue }: Props) {
  const color = fatigueColor(fatigue.overallFatigue);
  const recoveryColor = fatigueColor(100 - fatigue.recoveryIndex);
  const radius = 58;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (fatigue.overallFatigue / 100) * circumference;

  const severityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertOctagon size={12} className="text-red-500" />;
      case 'warning':
        return <AlertTriangle size={12} className="text-amber-500" />;
      default:
        return <Info size={12} className="text-blue-400" />;
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
      <h3 className="text-sm font-bold mb-1">Indeks zmęczenia</h3>
      <p className="text-[10px] text-slate-400 mb-3">
        Oszacowanie zmęczenia na podstawie intensywności, częstotliwości i regeneracji
      </p>

      <div className="flex items-start gap-6">
        {/* ── Circular gauge ────────────── */}
        <div className="relative flex-shrink-0">
          <svg width="140" height="140" className="transform -rotate-90">
            {/* Background track */}
            <circle
              cx="70"
              cy="70"
              r={radius}
              fill="none"
              stroke="currentColor"
              className="text-slate-100 dark:text-slate-800"
              strokeWidth="10"
            />
            {/* Fatigue arc */}
            <circle
              cx="70"
              cy="70"
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              className="transition-all duration-700 ease-out"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold" style={{ color }}>
              {fatigue.overallFatigue}
            </span>
            <span className="text-[10px] text-slate-400 -mt-0.5">/ 100</span>
          </div>
        </div>

        {/* ── Stats ───────────────────── */}
        <div className="flex-1 space-y-3 text-xs">
          {/* Recovery index */}
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-slate-500 flex items-center gap-1">
                <Shield size={12} /> Indeks regeneracji
              </span>
              <span className="font-bold" style={{ color: fatigueColor(100 - fatigue.recoveryIndex) }}>
                {fatigue.recoveryIndex}%
              </span>
            </div>
            <div className="h-1.5 rounded bg-slate-100 dark:bg-slate-800 overflow-hidden">
              <div
                className="h-full rounded transition-all duration-500"
                style={{
                  width: `${fatigue.recoveryIndex}%`,
                  backgroundColor: fatigueColor(100 - fatigue.recoveryIndex),
                }}
              />
            </div>
          </div>

          {/* Fatigue level label */}
          <div
            className="text-center py-1.5 rounded-lg font-bold text-xs"
            style={{ backgroundColor: color + '15', color }}
          >
            {fatigue.overallFatigue <= 30
              ? '✓ Niskie zmęczenie - Dobra regeneracja'
              : fatigue.overallFatigue <= 55
              ? '◉ Umiarkowane zmęczenie - Pod kontrolą'
              : fatigue.overallFatigue <= 75
              ? '⚠ Wysokie zmęczenie - Obserwuj'
              : '⛔ Bardzo wysokie - Zmniejsz obciążenie'}
          </div>

          {/* Suggestions count */}
          <div className="text-slate-400">
            {fatigue.warnings.length} ostrzeżeń ·{' '}
            {fatigue.suggestions.length} sugestii
          </div>
        </div>
      </div>

      {/* ── Warnings list ───────────────── */}
      {fatigue.warnings.length > 0 && (
        <div className="mt-4 space-y-1.5">
          <h4 className="text-[10px] font-semibold text-slate-400 uppercase">Ostrzeżenia</h4>
          {fatigue.warnings.map((w, i) => (
            <div
              key={i}
              className={`flex items-start gap-2 text-xs p-2 rounded-lg ${
                w.severity === 'critical'
                  ? 'bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300'
                  : w.severity === 'warning'
                  ? 'bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300'
                  : 'bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300'
              }`}
            >
              {severityIcon(w.severity)}
              <span>{w.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Suggestions ─────────────────── */}
      {fatigue.suggestions.length > 0 && (
        <div className="mt-3 space-y-1">
          <h4 className="text-[10px] font-semibold text-slate-400 uppercase">Sugestie</h4>
          {fatigue.suggestions.map((s, i) => (
            <div key={i} className="text-[11px] text-slate-600 dark:text-slate-400 pl-4 relative">
              <span className="absolute left-0 top-0.5 text-brand-500">›</span>
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
